import PropTypes from "prop-types";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Grid,
} from "@mui/material";

import "./FormResponseTable.scss";

import TablePagination from "../../../components/TablePagination/TablePagination";
import NoDataAvailable from "../../../components/NoDataAvailable/NoDataAvailable";
import Loader from "../../../components/Loader/Loader";
import { font_13 } from "../../../utils/utils";
import IconButtons from "../../../components/IconButtons/IconButtons";
import BootstrapTooltip from "../../../components/BootstrapTooltip/BootstrapTooltip";


const DEFAULT_COLUMN_WIDTH = 208;
const DEFAULT_MAX_WIDTH = 320;

const getStickyLeft = (index, columns) =>
  columns
    .slice(0, index)
    .reduce((acc, col) => acc + (col.minWidth || DEFAULT_COLUMN_WIDTH), 0);



const TableHeaderCell = ({ col, colIndex, columns, fixedColumnsCount }) => {
  const label = col.label || "";
  const displayLabel = label.length > 25 ? `${label.slice(0, 25)}...` : label;
  const tooltipText = col.tooltip ? `${label} - ${col.tooltip}` : label;
  const isSticky = colIndex < fixedColumnsCount;

  return (
    <TableCell
      key={col.key}
      align={col.align || "left"}
      sx={{
        minWidth: col.minWidth || DEFAULT_COLUMN_WIDTH,
        maxWidth: col.maxWidth || DEFAULT_MAX_WIDTH,
        position: isSticky ? "sticky" : "static",
        top: 0,
        left: isSticky ? getStickyLeft(colIndex, columns) : "auto",
        backgroundColor: "#fff",
        zIndex: isSticky ? 10 : 11,
      }}
      className="padding-left-1"
    >
      <BootstrapTooltip title={tooltipText}>
        <Typography variant="font_13_bold">{displayLabel}</Typography>
      </BootstrapTooltip>
    </TableCell>
  );
};


const TableBodyCell = ({ col, colIndex, row, columns, fixedColumnsCount }) => {
  const isSticky = colIndex < fixedColumnsCount;

  return (
    <TableCell
      key={col.key}
      align={col.align || "left"}
      className="padding-left-1"
      sx={{
        position: isSticky ? "sticky" : "static",
        left: isSticky ? getStickyLeft(colIndex, columns) : "auto",
        backgroundColor: "#fff",
        zIndex: isSticky ? 4 : 1,
      }}
    >
      <Typography variant="font_12">
        {typeof col.render === "function"
          ? col.render(row[col.key], row)
          : row[col.key] ?? "-"}
      </Typography>
    </TableCell>
  );
};


const ActionsCell = ({ actions, row, fixedColumnsCount, columns }) => {
  return (
    <TableCell
      align="center"
      className="padding-left-1 padding-right-1"
      sx={{
        minWidth: DEFAULT_COLUMN_WIDTH,
        maxWidth: DEFAULT_MAX_WIDTH,
        position: fixedColumnsCount ? "sticky" : "static",
        left: getStickyLeft(fixedColumnsCount, columns),
        backgroundColor: "#fff",
        zIndex: 7,
      }}
    >
      <Box className="topic-action-icons">
        {actions.map((action, index) => {
          const isDisabled = typeof action.disabled === "function"
            ? action.disabled(row)
            : action.disabled;
          const currentTooltip = typeof action.tooltip === "function"
            ? action.tooltip(row)
            : action.tooltip;
          const currentDisabledTooltip = typeof action.disabledTooltip === "function"
            ? action.disabledTooltip(row)
            : action.disabledTooltip;

          return (
            <IconButtons
              key={action.key ?? index}
              tooltip={currentTooltip}
              image={action.icon}
              handleClick={() => action.onClick(row)}
              disabled={isDisabled}
              disabledImage={action.disabledImage}
              disabledTooltip={currentDisabledTooltip}
              width={font_13}
              height={font_13}
              classList="margin-right-1"
            />
          );
        })}
      </Box>
    </TableCell>
  );
};

const TableDataRow = ({
  row,
  columns,
  actions,
  fixedColumnsCount,
  hasActions,
}) => {
  return (
    <TableRow className="table-body-row" hover>
      {columns.map((col, colIndex) => (
        <TableBodyCell
          key={col.key}
          col={col}
          colIndex={colIndex}
          row={row}
          columns={columns}
          fixedColumnsCount={fixedColumnsCount}
        />
      ))}
      {hasActions && (
        <ActionsCell
          actions={actions}
          row={row}
          fixedColumnsCount={fixedColumnsCount}
          columns={columns}
        />
      )}
    </TableRow>
  );
};


const TableHeader = ({ columns, hasActions, fixedColumnsCount }) => {
  return (
    <TableHead className="table-head-container">
      <TableRow className="table-head-row">
        {columns.map((col, colIndex) => (
          <TableHeaderCell
            key={col.key}
            col={col}
            colIndex={colIndex}
            columns={columns}
            fixedColumnsCount={fixedColumnsCount}
          />
        ))}
        {hasActions && (
          <TableCell
            align="center"
            sx={{
              minWidth: DEFAULT_COLUMN_WIDTH,
              maxWidth: DEFAULT_MAX_WIDTH,
              position: fixedColumnsCount ? "sticky" : "static",
              left: getStickyLeft(fixedColumnsCount, columns),
              backgroundColor: "#fff",
              zIndex: 6,
            }}
            className="padding-left-1 padding-right-1"
          >
            <Typography variant="font_13_bold">Actions</Typography>
          </TableCell>
        )}
      </TableRow>
    </TableHead>
  );
};

const TableBodyContent = ({
  rows,
  columns,
  actions,
  fixedColumnsCount,
  hasActions,
  hasRows,
  title,
}) => {
  if (!hasRows) {
    return (
      <TableRow>
        <TableCell
          colSpan={columns.length + (hasActions ? 1 : 0)}
          align="center"
        >
          <NoDataAvailable
            imgHeight={4}
            imgWidth={4.5}
            message={`No ${title} Available`}
          />
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {rows.map((row) => (
        <TableDataRow
          key={row.id || row.newerId || JSON.stringify(row)}
          row={row}
          columns={columns}
          actions={actions}
          fixedColumnsCount={fixedColumnsCount}
          hasActions={hasActions}
        />
      ))}
    </>
  );
};


const TablePaginationSection = ({
  hasRows,
  totalResult,
  totalPages,
  currentPage,
  handlePageChange,
}) => {
  if (!hasRows) return null;

  return (
    <Grid
      container
      className="table-pagination-container"
      justifyContent="space-between"
      alignItems="center"
    >
      <Typography variant="font_16">Total {totalResult} Records</Typography>
      <TablePagination
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        currentPage={currentPage}
      />
    </Grid>
  );
};

// -----------------------------
// Main Component
// -----------------------------
function FormResponseTable({
  title = "Records",
  columns = [],
  rows = [],
  totalResult = 0,
  totalPages = 0,
  currentPage = 1,
  handlePageChange,
  actions = [],
  isLoading = false,
  fixedColumnsCount = 0,
}) {
  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <Loader />
      </Box>
    );
  }

  const hasActions = actions.length > 0;
  const hasRows = rows.length > 0;
  return (
    <>
      <TableContainer className="table-container">
        <Table stickyHeader aria-label="dynamic table">
          <TableHeader
            columns={columns}
            hasActions={hasActions}
            fixedColumnsCount={fixedColumnsCount}
          />
          <TableBody>
            <TableBodyContent
              rows={rows}
              columns={columns}
              actions={actions}
              fixedColumnsCount={fixedColumnsCount}
              hasActions={hasActions}
              hasRows={hasRows}
              title={title}
            />
          </TableBody>
        </Table>
      </TableContainer>

      <TablePaginationSection
        hasRows={hasRows}
        totalResult={totalResult}
        totalPages={totalPages}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
      />
    </>
  );
}

// -----------------------------
// Prop Types
// -----------------------------
FormResponseTable.propTypes = {
  title: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string,
      tooltip: PropTypes.string,
      minWidth: PropTypes.number,
      maxWidth: PropTypes.number,
      align: PropTypes.string,
      render: PropTypes.func,
    })
  ),
  rows: PropTypes.array,
  totalResult: PropTypes.number,
  totalPages: PropTypes.number,
  currentPage: PropTypes.number,
  handlePageChange: PropTypes.func.isRequired,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      icon: PropTypes.string,
      tooltip: PropTypes.string,
      onClick: PropTypes.func.isRequired,
      disabled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
      disabledImage: PropTypes.string,
      disabledTooltip: PropTypes.string,
    })
  ),
  isLoading: PropTypes.bool,
  fixedColumnsCount: PropTypes.number,
};

export default FormResponseTable;
